import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { cn } from "@vd/ui";
import { ChevronRight, SquareTerminal } from "lucide-react";
import { useMemo, useState } from "react";
import { z } from "zod";
import { companiesQuery, logsInfinite } from "../../api/ops";
import type { LogLevel, OpsLogEvent } from "../../api/types";
import { Button, EmptyState, ErrorState, Loading, PageHeader, Select } from "../../components/ui";
import { fmtTime } from "../../lib/format";
import { useNavigate } from "../../lib/navigate";

const searchSchema = z.object({
  company: z.string().optional().catch(undefined),
  level: z.enum(["debug", "info", "warn", "error"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/logs")({
  staticData: { crumb: { label: "Logs", parents: [] } },
  validateSearch: (s) => searchSchema.parse(s),
  component: LogsPage,
});

/** How often the page asks for new events while it is open. */
const LOG_REFRESH_MS = 5_000;

function LogsPage() {
  const { company, level } = Route.useSearch();
  const navigate = useNavigate();
  const companies = useQuery(companiesQuery);
  const q = useInfiniteQuery({ ...logsInfinite(company, level), refetchInterval: LOG_REFRESH_MS });
  const rows = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);
  const set = (next: { company?: string | undefined; level?: LogLevel | undefined }) =>
    navigate({ to: "/logs", search: { company, level, ...next }, replace: true });

  return (
    <>
      <PageHeader title="Logs" />
      <div className="mb-3 flex flex-wrap gap-2">
        <Select
          aria-label="Company"
          className="w-56"
          value={company ?? ""}
          onValueChange={(v) => set({ company: v || undefined })}
          options={[
            { value: "", label: "All companies" },
            ...(companies.data?.items ?? []).map((c) => ({ value: c.id, label: c.name })),
          ]}
        />
        <Select
          aria-label="Level"
          className="w-40"
          value={level ?? ""}
          onValueChange={(v) => set({ level: (v || undefined) as LogLevel | undefined })}
          options={[
            { value: "", label: "All levels" },
            { value: "info", label: "Info and up" },
            { value: "warn", label: "Warnings and up" },
            { value: "error", label: "Errors" },
          ]}
        />
      </div>
      <div className="overflow-hidden rounded-lg bg-surface-1 shadow-[inset_0_0_0_1px_var(--vd-hairline)]">
        {q.isPending ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState icon={<SquareTerminal />} title="No events" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <div role="log" aria-live="off" className="min-w-[760px] divide-y divide-hairline">
                {rows.map((e) => (
                  <LogLine key={e.id} e={e} />
                ))}
              </div>
            </div>
            {q.hasNextPage ? (
              <div className="flex justify-center border-hairline border-t p-3">
                <Button size="sm" onClick={() => q.fetchNextPage()} busy={q.isFetchingNextPage}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </>
  );
}

const LEVEL_TEXT: Record<LogLevel, string> = {
  debug: "text-log-debug",
  info: "text-log-info",
  warn: "text-log-warn",
  error: "text-log-error",
};

function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex h-[18px] items-center rounded-xs bg-fill-2 px-1.5 text-[11px] text-ink-2">
      {children}
    </span>
  );
}

/** One event (DESIGN.md "Log stream rows"), with the company it belongs to, or Server. */
function LogLine({ e }: { e: OpsLogEvent }) {
  const [open, setOpen] = useState(false);
  const hasData = e.data !== undefined && e.data !== null;
  return (
    <div
      className={cn(
        "font-mono text-[12px] leading-[18px]",
        e.level === "error" && "bg-danger-soft/40 shadow-[inset_2px_0_0_0_var(--vd-log-error)]",
        e.level === "warn" && "shadow-[inset_2px_0_0_0_var(--vd-log-warn)]",
      )}
    >
      <button
        type="button"
        aria-expanded={hasData ? open : undefined}
        onClick={() => hasData && setOpen((o) => !o)}
        className={cn(
          "grid min-h-7 w-full grid-cols-[14px_96px_44px_128px_minmax(120px,180px)_1fr_auto] items-center gap-3 px-3 text-left",
          hasData ? "hover:bg-fill-1" : "cursor-default",
        )}
      >
        <ChevronRight
          aria-hidden
          className={cn(
            "size-3 text-ink-4 transition-transform duration-150",
            open && "rotate-90",
            !hasData && "invisible",
          )}
        />
        <span className="text-ink-3 tnum">{fmtTime(e.ts)}</span>
        <span className={cn("font-medium", LEVEL_TEXT[e.level])}>{e.level}</span>
        <span className={cn("truncate font-sans", e.company ? "text-ink" : "text-ink-3")}>
          {e.company?.name ?? "Server"}
        </span>
        <span className="truncate text-ink-2">{e.kind}</span>
        <span className="truncate text-ink">{e.message}</span>
        <span className="flex items-center gap-1">
          {e.call_id ? <Chip>call {e.call_id.slice(-6)}</Chip> : null}
          {e.app_version ? <Chip>{e.app_version}</Chip> : null}
        </span>
      </button>
      {open && hasData ? (
        <pre className="mx-3 mb-2 ml-[29px] overflow-x-auto rounded-sm bg-well p-3 text-[11px] text-ink-2 leading-4 shadow-[inset_0_0_0_1px_var(--vd-hairline)]">
          {JSON.stringify(e.data, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}
